import { AnimatePresence } from 'framer-motion';
import { FC, MouseEvent, useState } from 'react';
import styled from 'styled-components';

import Button from '@/components/Button';
import Checkbox from '@/components/Checkbox';
import { Input, Textarea } from '@/components/Forms';
import ShowHideButton from '@/components/ShowHideButton';
import Text from '@/components/Text';

import AttachedCountBadge from '../AttachedCountBadge';
import MoreImagesModal from '../MoreImagesModal';
import UploadMultipleImages from '../UploadMultipleImages';
import {
  AddMoreButton,
  Box,
  ButtonHolder,
  Header,
  HeaderButtons,
  Heading,
  ImageData,
  LabelText,
  MoreImages,
  ResetButton,
  UploadedImages,
} from '../VariationOne';
import {
  ImageGrid,
  ImagesBox,
  TextAreaBox,
  TextPlaceholder,
} from '../VariationTwo';
import { Filler } from '../shared';
import { getWatermarkPosition, getWatermarkTag } from '../shared';
import { ActionBox, ActionTitle } from '../shared/styledComponents';
import GeneratedImages from './GeneratedImages';
import UploadedImageRow from './UploadedImageRow';

export interface ImageVariation {
  leftImageSrc: string;
  leftWatermarkTag: string;
  leftWatermarkPosition: string;
  rightText: string;
  brakes: boolean;
  fontSize?: number;
}

const StyledUploadedImages = styled(UploadedImages)`
  height: 382px;
`;

const StyledTextArea = styled(Textarea)`
  height: 168px;
`;

const StyledUploadMultipleImages = styled(UploadMultipleImages)`
  height: 358px;
`;

interface Props {
  inArticle?: boolean;
}

const VariationTypeIt: FC<Props> = ({ inArticle }) => {
  const [showContent, setShowContent] = useState(true);
  const [text, setText] = useState('');
  const [brakes, setBrakes] = useState(false);
  const [leftImages, setLeftImages] = useState<ImageData[]>([]);
  const [fontSize, setFontSize] = useState('');
  const [showMoreImagesModal, setShowMoreImagesModal] = useState(false);
  const [variations, setVariations] = useState<ImageVariation[]>([]);
  const [images, setImages] = useState<string[]>();

  const onReset = () => {
    setLeftImages([]);
    setVariations([]);
    setImages([]);
    setText('');
    setBrakes(false);
    setFontSize('');
  };

  const generateVariations = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    const newVariations = leftImages.map((leftImage) => ({
      leftWatermarkTag: getWatermarkTag(leftImage),
      leftWatermarkPosition: getWatermarkPosition(leftImage),
      leftImageSrc:
        leftImage.croppedImage ?? URL.createObjectURL(leftImage.image),
      rightText: text,
      brakes: brakes,
      fontSize: parseInt(fontSize),
    }));

    setVariations(newVariations);
  };

  const renderLeftImages = leftImages.map((image, index) => (
    <UploadedImageRow
      key={`${index}-${image.image.name}-${image.image.lastModified}`}
      imageIndex={index}
      image={image}
      setImages={setLeftImages}
    />
  ));

  const isResetDisabled = !leftImages?.length && !text && !brakes && !fontSize;

  return (
    <>
      <Filler fontFamily="MyriadProRegular" fontWeight={400} />
      <Box>
        <Header>
          <div>
            <Heading $type="button">Type It</Heading>
            <Text $type="smallHeadline" $color="gray">
              Attach one image on the left side and add text on the right with
              the necessary parameters
            </Text>
          </div>
          <HeaderButtons>
            <ResetButton
              color="red"
              disabled={isResetDisabled}
              onClick={onReset}
            >
              Reset
            </ResetButton>
            <ShowHideButton
              showContent={showContent}
              onClick={() => setShowContent((prev) => !prev)}
            />
          </HeaderButtons>
        </Header>
        {showContent && (
          <ImageGrid>
            <ImagesBox $hasImages={!!leftImages?.length}>
              <LabelText $type="smallHeadline">Left Images</LabelText>
              {!!leftImages?.length ? (
                <>
                  <StyledUploadedImages>
                    {renderLeftImages}
                  </StyledUploadedImages>
                  <MoreImages>
                    <AttachedCountBadge count={leftImages.length} />
                    <AddMoreButton
                      onClick={() => setShowMoreImagesModal(true)}
                      color="colorized"
                    >
                      Add more images
                    </AddMoreButton>
                  </MoreImages>
                </>
              ) : (
                <StyledUploadMultipleImages
                  coverIndex={2}
                  setImages={setLeftImages}
                />
              )}
            </ImagesBox>
            <div>
              <TextAreaBox>
                <div>
                  <LabelText $type="smallHeadline">Right Text</LabelText>
                  <StyledTextArea
                    onChange={(e) => setText(e.target.value)}
                    value={text}
                    placeholder="Add text"
                  ></StyledTextArea>
                </div>
                <div>
                  <LabelText $type="smallHeadline">Font Size</LabelText>
                  <Input
                    value={fontSize}
                    onChange={(e) => setFontSize(e.target.value)}
                    placeholder="Font Size (px)"
                    type="number"
                    min="0"
                  />
                </div>
                <div>
                  <ActionTitle $type="smallHeadline">
                    Text Parameters
                  </ActionTitle>
                  <ActionBox>
                    <Checkbox
                      checked={brakes}
                      onChange={() => setBrakes((prev) => !prev)}
                    >
                      Custom brakes
                    </Checkbox>
                  </ActionBox>
                </div>
              </TextAreaBox>
              {!!leftImages?.length && <TextPlaceholder />}
            </div>
          </ImageGrid>
        )}
      </Box>
      {showContent && (
        <ButtonHolder>
          <Button disabled={!leftImages?.length} onClick={generateVariations}>
            {!!variations?.length ? 'Regenerate' : 'Generate'}
          </Button>
        </ButtonHolder>
      )}
      <GeneratedImages
        variations={variations}
        images={images}
        setImages={setImages}
        inArticle={inArticle}
        onReset={onReset}
      />
      <AnimatePresence>
        {!!showMoreImagesModal && (
          <MoreImagesModal
            onClose={() => setShowMoreImagesModal(false)}
            setImages={setLeftImages}
            coverIndex={1}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default VariationTypeIt;
